/**
 * FeedbackEditDialog - Edit feedback request details
 * 
 * Allows editing:
 * - Due date
 * - Add/remove stakeholders (only those who haven't responded)
 * - Delete the entire request (only if no responses received)
 */

import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Calendar,
  UserPlus,
  Trash2,
  X,
  AlertTriangle,
  Check,
  User,
  Mail,
  Building2,
  Loader2,
  Users
} from 'lucide-react'
import { feedbackService, FeedbackRequest, FeedbackStakeholder, FeedbackCategory } from '@/services/api/feedbackService'
import { EmployeePicker } from './EmployeePicker'
import { feedbackDueDay } from './feedbackDueDate'
import { sastToday } from '@/components/admin/lifecycle/spellCoverage'
import toast from 'react-hot-toast'

interface InternalStakeholder {
  id: string
  name: string
  role: string
  department: string
  campus?: string
  email?: string
}

interface FeedbackEditDialogProps {
  open: boolean
  onClose: () => void
  request: FeedbackRequest
  onUpdate: () => void  // Callback to refresh parent data
  currentUserId?: string
}

export default function FeedbackEditDialog({
  open,
  onClose,
  request,
  onUpdate,
  currentUserId
}: FeedbackEditDialogProps) {
  const [dueDate, setDueDate] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [stakeholderTab, setStakeholderTab] = useState<'internal' | 'external'>('internal')
  const [removingStakeholderId, setRemovingStakeholderId] = useState<string | null>(null)
  
  // Internal stakeholders (using EmployeePicker)
  const [selectedInternalStakeholders, setSelectedInternalStakeholders] = useState<InternalStakeholder[]>([])
  const [addingInternalStakeholder, setAddingInternalStakeholder] = useState(false)
  
  // External stakeholder form
  const [showAddExternal, setShowAddExternal] = useState(false)
  const [externalName, setExternalName] = useState('')
  const [externalEmail, setExternalEmail] = useState('')
  const [externalOrg, setExternalOrg] = useState('')
  const [externalCategory, setExternalCategory] = useState<'PEER' | 'CLIENT' | 'PARTNER' | 'OTHER'>('PEER')
  const [emailError, setEmailError] = useState('')
  const [addingExternalStakeholder, setAddingExternalStakeholder] = useState(false)

  // Initialize due date from request, as its South African day
  useEffect(() => {
    if (request?.due_date) {
      setDueDate(feedbackDueDay(request.due_date) ?? '')
    }
  }, [request])

  const handleSaveDueDate = async () => {
    if (!dueDate || !request.id) return
    
    setSaving(true)
    try {
      await feedbackService.updateFeedbackRequest(request.id, {
        // The picked day at midnight with no time zone, as creating the
        // request stores it. The column has no time zone and the database
        // driver rejects a zoned value, so a zoned update never saved.
        due_date: `${dueDate}T00:00:00`
      })
      toast.success('Due date updated successfully')
      onUpdate()
    } catch (error: any) {
      console.error('Failed to update due date:', error)
      toast.error(error.response?.data?.detail || 'Failed to update due date')
    } finally {
      setSaving(false)
    }
  }

  // Handle adding internal stakeholders when selected from EmployeePicker
  const handleInternalStakeholderSelection = async (employees: InternalStakeholder[]) => {
    if (!request.id) return
    
    // Find newly added employees
    const newEmployees = employees.filter(
      emp => !selectedInternalStakeholders.some(s => s.id === emp.id)
    )
    
    // Add each new employee as a stakeholder
    setAddingInternalStakeholder(true)
    for (const emp of newEmployees) {
      try {
        await feedbackService.addStakeholder(request.id, {
          stakeholder_name: emp.name,
          stakeholder_email: emp.email,
          stakeholder_user_id: emp.id,
          stakeholder_role: emp.role,
          category: 'peer'  // Default category for internal stakeholders
        })
      } catch (error: any) {
        console.error('Failed to add stakeholder:', error)
        toast.error(`Failed to add ${emp.name}`)
      }
    }
    
    setSelectedInternalStakeholders(employees)
    setAddingInternalStakeholder(false)
    
    if (newEmployees.length > 0) {
      toast.success(`Added ${newEmployees.length} stakeholder(s)`)
      onUpdate()
    }
  }
  
  // Handle adding external stakeholder
  const handleAddExternalStakeholder = async () => {
    if (!externalName.trim() || !externalEmail.trim() || !externalOrg.trim() || !request.id) {
      toast.error('Please fill in all required fields')
      return
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(externalEmail.trim())) {
      setEmailError('Invalid email format')
      return
    }
    
    setAddingExternalStakeholder(true)
    try {
      await feedbackService.addStakeholder(request.id, {
        stakeholder_name: externalName.trim(),
        stakeholder_email: externalEmail.trim(),
        stakeholder_role: externalOrg.trim(),
        category: 'external'
      })
      toast.success('External stakeholder added successfully')
      
      // Reset form
      setExternalName('')
      setExternalEmail('')
      setExternalOrg('')
      setExternalCategory('PEER')
      setShowAddExternal(false)
      onUpdate()
    } catch (error: any) {
      console.error('Failed to add external stakeholder:', error)
      toast.error(error.response?.data?.detail || 'Failed to add stakeholder')
    } finally {
      setAddingExternalStakeholder(false)
    }
  }

  const handleRemoveStakeholder = async (stakeholder: FeedbackStakeholder) => {
    if (stakeholder.response_status === 'completed') {
      toast.error('Cannot remove stakeholder who has already responded')
      return
    }
    if (!request.id || !stakeholder.id) return
    
    setRemovingStakeholderId(stakeholder.id)
    try {
      await feedbackService.removeStakeholder(request.id, stakeholder.id)
      toast.success('Stakeholder removed successfully')
      onUpdate()
    } catch (error: any) {
      console.error('Failed to remove stakeholder:', error)
      toast.error(error.response?.data?.detail || 'Failed to remove stakeholder')
    } finally {
      setRemovingStakeholderId(null)
    }
  }

  const handleDeleteRequest = async () => {
    if (!request.id) return
    setDeleting(true)
    try {
      await feedbackService.deleteFeedbackRequest(request.id)
      toast.success('Feedback request deleted successfully')
      onClose()
      onUpdate()
    } catch (error: any) {
      console.error('Failed to delete request:', error)
      toast.error(error.response?.data?.detail || 'Failed to delete request')
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const canDeleteRequest = (request.responses_received ?? 0) === 0

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'peer': return 'bg-blue-100 text-blue-800'
      case 'direct_report': return 'bg-green-100 text-green-800'
      case 'external': return 'bg-purple-100 text-purple-800'
      case 'campus_gm': return 'bg-amber-100 text-amber-800'
      case 'project_manager': return 'bg-indigo-100 text-indigo-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Feedback Request
          </DialogTitle>
          <DialogDescription>
            Modify due date, manage stakeholders, or delete this request
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Request Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900">{request.title}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {request.total_stakeholders} stakeholders • {request.responses_received} responded
            </p>
          </div>

          {/* Due Date Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Recommended due date
            </Label>
            <div className="flex gap-2">
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="flex-1"
                min={sastToday()}
              />
              <Button 
                onClick={handleSaveDueDate} 
                disabled={saving || !dueDate}
                size="sm"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </Button>
            </div>
          </div>

          <Separator />

          {/* Stakeholders Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Stakeholders ({request.stakeholders?.length || 0})
            </Label>

            {/* Add Stakeholder Tabs */}
            <Tabs value={stakeholderTab} onValueChange={(v) => setStakeholderTab(v as 'internal' | 'external')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="internal">
                  <Users className="h-4 w-4 mr-2" />
                  Internal
                </TabsTrigger>
                <TabsTrigger value="external">
                  <UserPlus className="h-4 w-4 mr-2" />
                  External
                </TabsTrigger>
              </TabsList>

              {/* Internal Stakeholders Tab - EmployeePicker */}
              <TabsContent value="internal" className="space-y-3">
                <p className="text-xs text-gray-500">Search and add internal employees as stakeholders</p>
                <EmployeePicker
                  selectedEmployees={selectedInternalStakeholders}
                  onSelectionChange={handleInternalStakeholderSelection}
                  placeholder="Type to search employees by name, role, or department..."
                  excludeUserId={currentUserId}
                />
                {addingInternalStakeholder && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Adding stakeholders...
                  </div>
                )}
              </TabsContent>

              {/* External Stakeholders Tab */}
              <TabsContent value="external" className="space-y-3">
                <p className="text-xs text-gray-500">Add external stakeholders who are not in the team member directory</p>
                
                {!showAddExternal ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddExternal(true)}
                    className="w-full"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add External Stakeholder
                  </Button>
                ) : (
                  <Card className="border-dashed border-2 border-blue-200 bg-blue-50/50">
                    <CardContent className="p-4 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Name *</Label>
                          <Input
                            placeholder="Full name"
                            value={externalName}
                            onChange={(e) => setExternalName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email *</Label>
                          <Input
                            type="email"
                            placeholder="email@example.com"
                            value={externalEmail}
                            onChange={(e) => {
                              setExternalEmail(e.target.value)
                              setEmailError('')
                            }}
                            className={emailError ? 'border-red-500' : ''}
                          />
                          {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Organisation *</Label>
                          <Input
                            placeholder="Company/Institution"
                            value={externalOrg}
                            onChange={(e) => setExternalOrg(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Category</Label>
                          <Select value={externalCategory} onValueChange={(v) => setExternalCategory(v as typeof externalCategory)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PEER">Peer</SelectItem>
                              <SelectItem value="CLIENT">Client</SelectItem>
                              <SelectItem value="PARTNER">Partner</SelectItem>
                              <SelectItem value="OTHER">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowAddExternal(false)
                            setExternalName('')
                            setExternalEmail('')
                            setExternalOrg('')
                            setEmailError('')
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddExternalStakeholder}
                          disabled={addingExternalStakeholder || !externalName || !externalEmail || !externalOrg}
                        >
                          {addingExternalStakeholder ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <UserPlus className="h-4 w-4 mr-1" />
                          )}
                          Add Stakeholder
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            {/* Stakeholder List */}
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {request.stakeholders?.map((stakeholder) => (
                <div
                  key={stakeholder.id}
                  className="flex items-center justify-between p-3 bg-white border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{stakeholder.stakeholder_name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {stakeholder.stakeholder_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {stakeholder.stakeholder_email}
                          </span>
                        )}
                        {stakeholder.stakeholder_role && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {stakeholder.stakeholder_role}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getCategoryColor(stakeholder.category)}>
                      {stakeholder.category}
                    </Badge>
                    {stakeholder.response_status === 'completed' ? (
                      <Badge className="bg-green-100 text-green-800">Responded</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveStakeholder(stakeholder)}
                        disabled={removingStakeholderId === stakeholder.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        {removingStakeholderId === stakeholder.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Delete Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-4 w-4" />
              Danger Zone
            </Label>
            
            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!canDeleteRequest}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Feedback Request
              </Button>
            ) : (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm text-red-800">
                    Are you sure you want to delete this feedback request? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteRequest}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-1" />
                      )}
                      Delete Permanently
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {!canDeleteRequest && (
              <p className="text-xs text-gray-500">
                Cannot delete - {request.responses_received} response(s) already received
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
